import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LoadBreakdownView, LoadBreakdownForm, loadRpm } from "@/components/LoadBreakdown";
import { Truck, Eye, PencilSimple, Trash, UserPlus, Sparkle, ArrowLeft, CaretRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const LOAD_STATUSES = ["Pre-Booked", "Dispatched", "Arrived at Shipper", "In Transit", "Arrived at Receiver", "Delivered", "Cancelled", "TONU"];
const EDIT_KEYS = ["reference", "shipper_name", "shipper_address", "pickup_date", "pickup_time", "pickup_number", "appointment_number", "reference_number",
  "receiver_name", "receiver_address", "delivery_date", "delivery_time", "delivery_number", "map", "weight", "temp", "commodity", "equipment", "comment"];
const NUM_KEYS = ["dead_head_miles", "loaded_miles", "total_miles", "rate", "rpm"];
const city = (a) => (a || "").split(",").slice(-2).join(",").trim() || "—";

function AssignDriverDialog({ load, drivers, onClose, onDone }) {
  const [driverId, setDriverId] = useState("");
  const list = drivers.filter((d) => d.carrier_id === load?.carrier_id);
  const save = async () => {
    const d = list.find((x) => x.id === driverId);
    try { await api.put(`/loads/${load.id}`, { driver: d.name, driver_id: d.id }); toast.success(`Assigned to ${d.name}. The driver can now see this load.`); onDone(); onClose(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={!!load} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="assign-driver-dialog">
        <DialogHeader><DialogTitle className="font-heading">Assign Driver</DialogTitle><DialogDescription className="text-slate-500">Load {load?.reference} · drivers of this carrier</DialogDescription></DialogHeader>
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="bg-white border-slate-300" data-testid="assign-driver-select"><SelectValue placeholder="Select a driver…" /></SelectTrigger>
          <SelectContent>{list.length === 0 ? <SelectItem value="none" disabled>No drivers for this carrier. Add them in Driver Info.</SelectItem> : list.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.truck ? ` · Truck ${d.truck}` : ""}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
          <Button onClick={save} disabled={!driverId} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="assign-driver-confirm">Assign Driver</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BreakdownDialog({ load, startEdit, isAdmin, onClose, onDone }) {
  const [edit, setEdit] = useState(startEdit);
  const [form, setForm] = useState(load);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(EDIT_KEYS.map((k) => [k, form[k] ?? ""]));
      NUM_KEYS.forEach((k) => { body[k] = Number(form[k]) || 0; });
      body.origin = form.shipper_address || form.origin || ""; body.destination = form.receiver_address || form.destination || "";
      body.po_number = form.reference || "";
      if (isAdmin) { body.dispatch_charges = Number(form.dispatch_charges) || 0; body.dispatch_invoice = form.dispatch_invoice || ""; }
      await api.put(`/loads/${load.id}`, body);
      toast.success("Load updated"); onDone(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="load-breakdown-dialog">
        <DialogHeader><DialogTitle className="font-heading">Load {load.reference}</DialogTitle><DialogDescription className="text-slate-500">{load.carrier_name || ""} · {load.status}</DialogDescription></DialogHeader>
        {edit ? (
          <>
            <LoadBreakdownForm value={form} onChange={setForm} />
            {isAdmin && (
              <div className="grid sm:grid-cols-2 gap-3 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="load-admin-fields">
                <p className="sm:col-span-2 text-[11px] font-bold uppercase tracking-wide text-amber-700">Admin Only · Billing</p>
                <Field label="Dispatch Charges ($)"><Input type="number" value={form.dispatch_charges ?? ""} onChange={(e) => setForm({ ...form, dispatch_charges: e.target.value })} className="bg-white border-slate-300 font-mono" data-testid="load-dispatch-charges" /></Field>
                <Field label="Dispatch Invoice #"><Input value={form.dispatch_invoice ?? ""} onChange={(e) => setForm({ ...form, dispatch_invoice: e.target.value })} className="bg-white border-slate-300 font-mono" data-testid="load-dispatch-invoice" /></Field>
              </div>
            )}
          </>
        ) : <LoadBreakdownView load={load} />}
        <DialogFooter>
          {edit ? (
            <>
              <Button variant="outline" onClick={() => setEdit(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="load-save-btn">{saving ? "Saving…" : "Save Changes"}</Button>
            </>
          ) : <Button onClick={() => setEdit(true)} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="load-edit-btn"><PencilSimple size={15} className="mr-1.5" />Edit Breakdown</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Loads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const invalidate = useInvalidate();
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const { data: drivers = [] } = useList("drivers", "/drivers");
  const [carrierId, setCarrierId] = useState(null);
  const { data: loads = [], isLoading } = useList(["loads", carrierId], `/loads?carrier_id=${carrierId}`, { enabled: !!carrierId });
  const carrier = carriers.find((c) => c.id === carrierId);
  const [assigning, setAssigning] = useState(null);
  const [viewing, setViewing] = useState(null);
  const refresh = () => invalidate(["loads", "dashboard", "loads-map"]);
  const carrierName = (id) => carriers.find((c) => c.id === id)?.carrier_name || "—";

  const setStatus = async (l, status) => {
    try { await api.put(`/loads/${l.id}`, { status }); toast.success(`Load ${l.reference} → ${status}`); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete load ${l.reference}?`)) return;
    try { await api.delete(`/loads/${l.id}`); refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const th = "px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-slate-400";
  const td = "px-4 py-3 text-sm";
  return (
    <div>
      <PageHeader title="Load Management" subtitle="Loads come from Asterism AI. Assign a driver and track every load to delivery."
        actions={<Link to="/asterism-ai" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700" data-testid="loads-ai-link"><Sparkle size={16} weight="fill" />New Load via Asterism AI</Link>} />
      {!carrierId ? (
        carriers.length === 0 ? <Panel><EmptyState icon={Truck} title="No Active Carriers" hint="Authorized carriers appear here." /></Panel> : (
          <Panel className="p-0 overflow-hidden" data-testid="loads-carrier-grid">
            <div className="hidden md:grid grid-cols-[minmax(0,2.2fr)_repeat(4,minmax(0,1fr))_24px] gap-4 px-5 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              <span>Carrier</span><span>Drivers</span><span>In Transit</span><span>Delivered</span><span>Total Load Amount</span><span />
            </div>
            <div className="divide-y divide-slate-100">
              {carriers.map((c) => (
                <button key={c.id} onClick={() => setCarrierId(c.id)} data-testid={`loads-carrier-${c.id}`}
                  className="group w-full text-left grid grid-cols-2 md:grid-cols-[minmax(0,2.2fr)_repeat(4,minmax(0,1fr))_24px] gap-4 items-center px-5 py-3.5 hover:bg-blue-50/50 transition-colors">
                  <div className="col-span-2 md:col-span-1 flex items-center gap-3 min-w-0">
                    <span className="w-10 h-10 rounded-lg bg-slate-900 text-white grid place-items-center font-heading font-extrabold shrink-0">{(c.carrier_name || "?")[0].toUpperCase()}</span>
                    <div className="min-w-0"><p className="font-heading font-bold text-slate-900 truncate">{c.carrier_name}</p><p className="text-xs text-slate-500 truncate">MC {c.mc_number || "—"} · {c.assigned_dispatcher_name}</p></div>
                  </div>
                  <span className="text-sm text-slate-600"><b className="font-mono text-slate-900">{(c.drivers || []).length}</b><span className="md:hidden"> drivers</span></span>
                  <span className="text-sm text-slate-600"><b className="font-mono text-slate-900">{c.in_transit_loads ?? 0}</b><span className="md:hidden"> in transit</span></span>
                  <span className="text-sm text-slate-600"><b className="font-mono text-slate-900">{c.delivered_loads ?? 0}</b><span className="md:hidden"> delivered</span></span>
                  <span className="font-mono font-semibold text-slate-900">{money(c.total_load_amount)}</span>
                  <CaretRight size={18} className="hidden md:block text-slate-300 group-hover:text-blue-600 transition-colors" />
                </button>
              ))}
            </div>
          </Panel>
        )
      ) : (<>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => setCarrierId(null)} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-700 hover:border-blue-400" data-testid="loads-back"><ArrowLeft size={15} />All Carriers</button>
        <p className="font-heading font-bold text-slate-900 text-lg" data-testid="loads-carrier-title">{carrier?.carrier_name} · Load History</p>
        <span className="text-sm text-slate-500"><b className="text-slate-900 font-mono">{loads.length}</b> loads</span>
      </div>
      <Panel className="p-0 overflow-hidden">
        {isLoading ? <p className="p-6 text-slate-400">Loading…</p> : loads.length === 0 ? (
          <EmptyState icon={Truck} title="No Loads Yet" hint="Upload a rate confirmation in Asterism AI and assign it to a carrier." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50/60"><tr>{["Load #", "Shipper → Receiver", "Pickup", "Rate", "RPM", "Driver", "Status", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {loads.map((l) => (
                  <tr key={l.id} className="hover:bg-slate-50/70" data-testid={`load-row-${l.id}`}>
                    <td className={`${td} font-mono font-semibold text-blue-600`}>{l.reference}</td>
                    <td className={td}><p className="text-slate-900 font-medium">{l.shipper_name || city(l.origin)} <span className="text-slate-400">→</span> {l.receiver_name || city(l.destination)}</p><p className="text-xs text-slate-500">{city(l.shipper_address || l.origin)} → {city(l.receiver_address || l.destination)}</p></td>
                    <td className={`${td} text-slate-600 whitespace-nowrap`}>{l.pickup_date || "—"}{l.pickup_time ? ` · ${l.pickup_time}` : ""}</td>
                    <td className={`${td} font-mono font-semibold text-slate-900`}>{money(l.rate)}</td>
                    <td className={`${td} font-mono text-slate-600`}>{loadRpm(l) ? `$${loadRpm(l)}` : "—"}</td>
                    <td className={td}>
                      {l.driver ? (
                        <button onClick={() => setAssigning(l)} className="text-slate-900 font-medium hover:text-blue-600" data-testid={`load-driver-${l.id}`}>{l.driver}</button>
                      ) : (
                        <Button onClick={() => setAssigning(l)} className="h-8 bg-blue-600 text-white hover:bg-blue-700 text-xs font-semibold" data-testid={`assign-driver-${l.id}`}><UserPlus size={14} className="mr-1" />Assign Driver</Button>
                      )}
                    </td>
                    <td className={td}>
                      <Select value={LOAD_STATUSES.includes(l.status) ? l.status : undefined} onValueChange={(v) => setStatus(l, v)}>
                        <SelectTrigger className="h-8 w-40 bg-white border-slate-200 text-xs" data-testid={`load-status-${l.id}`}><SelectValue placeholder={l.status || "Set Status"} /></SelectTrigger>
                        <SelectContent>{LOAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className={`${td} text-right whitespace-nowrap`}>
                      <button onClick={() => setViewing({ load: l, edit: false })} className="p-1.5 text-slate-400 hover:text-blue-600" title="View Breakdown" data-testid={`load-view-${l.id}`}><Eye size={16} /></button>
                      <button onClick={() => setViewing({ load: l, edit: true })} className="p-1.5 text-slate-400 hover:text-blue-600" title="Edit" data-testid={`load-edit-${l.id}`}><PencilSimple size={16} /></button>
                      {isAdmin && <button onClick={() => remove(l)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`load-delete-${l.id}`}><Trash size={16} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      </>)}
      <AssignDriverDialog load={assigning} drivers={drivers} onClose={() => setAssigning(null)} onDone={refresh} />
      {viewing && <BreakdownDialog load={{ ...viewing.load, carrier_name: carrierName(viewing.load.carrier_id) }} startEdit={viewing.edit} isAdmin={isAdmin} onClose={() => setViewing(null)} onDone={refresh} />}
    </div>
  );
}
